/* dg-spin-o240.js: copy of dg-spin-v401.js (itself a copy of dg-spin.js) for the 49" 240 Hz OLED (DG49OLED240, body
   "49oled", assets dgs-49oled-mesh.bin / dgs-49oled-views.json / dgs-49oledo240-<view>.webp). dg-spin.js and
   dg-spin-v401.js are untouched (the running test and the 40" page use them). Changes vs dg-spin.js:
   - window.DG_TEX_O240 (object, view name -> URL), when set, replaces opts.textures: the page JS passes the 49hex view
     keys (front34r, rear34a, rear, ...) and this body's views are front, front34r, front34l, sidea, sideb, rear34a, rear34b.
   - phone maxDpr 2.5; textures mipmapped (LinearMipmapLinearFilter) with anisotropy 8, so the sharper renders stay sharp.
   - lit-screen overlay defaults = the 49oled active area (the wallpaper outline of the 7680 px front factory render
     DG49OLED240_factory_08, unprojected through the packaged front camera onto the panel's curved front surface):
     x -602.4..602.4, bottom 232.66, top 575.5 mm at the centre, top sagging 1.33 mm toward the ends (fit, residual
     < 0.3 mm; new optional screen.tiltTop / tiltBottom hold the photo's slight left-right tilt), checked on rendered
     frames (overlay filled solid over a wallpaper-textured front) so the lit area meets the inner edge of the panel's
     black border on every side. The overlay's depth profile is extrapolated past the glass ends (see zAt).
     The screen canvas takes the panel's aspect (~32:9), so painters drawing with (w, h) are not stretched.
   - a straight rear view: the packaged set has none (the back was two 3/4 renders, which smeared the stand post and
     cable clip over the shell between ~150 and ~210 deg). When the page passes a 'rear' texture for this body
     (dgs-49oledo240-rear.webp = DG49OLED240_factory_09), an 8th view is added with the camera fitted for it in the same
     fitting state as the packaged views (constant REAR). The hull shader's unused screenTex sampler is dropped so that
     8 views x 2 samplers fit 16 texture units; a GPU with fewer units keeps the 7 packaged views.
   - cleaner photo blending (as dg-spin-v401.js, retuned for this body): facing power 5 (6 without the rear view), blend
     widths rear 0.40 / rear 3/4 0.30 (0.70 without the rear view), visibility tested in millimetres, the front face
     (mesh parts 'screen' and 'bezel': glass, black border, chin and its logo) coloured from the straight front render,
     loose-test fallback for slivers. The lit overlay is depth-tested.
   Original dg-spin-v401.js header follows (its numbers are the 40hex body's). */
/* dg-spin-v401.js: copy of dg-spin.js for the 40" 5K2K (DGVIEW401, body "40hex", assets dgs-40hex-mesh.bin /
   dgs-40hex-views.json / dgs-40hexf-<view>.webp full-resolution factory renders). dg-spin.js itself is untouched
   (the running 165 Hz test uses it). Changes vs dg-spin.js:
   - window.DG_TEX_V401 (object, view name -> URL), when set, replaces opts.textures: the page JS passes the 49" view
     keys (front34r, rear34a, ...) and this body's views are front, front34l, rear34r, rear34l, rear, sidel, sider.
   - phone maxDpr 2.5; textures mipmapped (LinearMipmapLinearFilter) with anisotropy 8, so the full-res renders stay sharp.
   - lit-screen overlay defaults = the 40hex active panel: measured from the front factory render through its fitted
     camera, then trimmed against rendered frames so that at the landed view (az 0) the lit area meets the panel's
     inner bezel line within 1 px at 2x on every side: x -469.8..470.6, bottom 158.6, top 556.2 mm, edges following
     the photo's panel curve (top +2.7 mm, bottom -1.1 mm at the ends). The screen canvas takes the panel's aspect
     (~21:9, 1920 x 814), so painters drawing with (w, h) are not stretched. The lit overlay is depth-tested.
   - cleaner photo blending for this body (each change commented where it is made): per-view blend widths (3/4 renders
     0.40, others 0.55), visibility tested in millimetres (no see-through ghost of the stand post on the rear shell),
     the front face coloured from the straight front render (no second chin logo), loose-test fallback for slivers.
   Original header follows. */
/* Deco Gear photo-projected turntable (production build of proto/mock/duo/spin.js).
   The fitted 3D shape of the monitor is coloured by projecting the REAL factory renders onto it from the
   exact camera each render was taken from, with per-view visibility (depth maps) and view-dependent blending.
   Requires window.THREE (r128).

   Shopify asset URLs are flat CDN URLs, so every file is passed explicitly:
     DGSpin.mount(canvas, {
       mesh:     '{{ "dgs-49hex-mesh.bin" | asset_url }}',          // packed mesh (.bin; legacy model.json also accepted)
       views:    '{{ "dgs-49hex-views.json" | asset_url }}',        // camera poses
       textures: { front: '{{ "dgs-49hex-front.webp" | asset_url }}', ... },  // keyed by view name (falls back to base + view.img)
       screen:   { screenX: 583, screenY0: 214, screenY1: 555 },    // optional lit-screen overlay rectangle (mm)
       onLanded, preserve, maxDpr, depthRes
     }) -> Promise<{ play(o), setAz(deg), setPower(0..1), paintScreen(fn), screenLoop(fn), stopScreen(), draw(), destroy() }>
   Rendering pauses while the canvas is off-screen (or the tab is hidden) and resumes on return. */
(function () {
  const VS = `
    varying vec3 vW; varying vec3 vN; varying vec2 vUv;
    void main(){ vec4 w = modelMatrix*vec4(position,1.); vW=w.xyz; vN=normalize(mat3(modelMatrix)*normal); vUv=uv;
      gl_Position = projectionMatrix*viewMatrix*w; }`;
  const FS = (n) => `
    #include <packing>
    uniform sampler2D tex[${n}]; uniform sampler2D dep[${n}]; uniform mat4 VP[${n}]; uniform vec3 camP[${n}];
    uniform vec3 eye; uniform float isScreen; uniform float power; uniform float sig[${n}]; uniform float fpow;
    uniform vec2 nf[${n}]; uniform vec4 dpar; uniform float pri[${n}];
    varying vec3 vW; varying vec3 vN; varying vec2 vUv;
    float lz(float d, vec2 q){ return 2.*q.x*q.y / ((q.y+q.x) - (2.*d-1.)*(q.y-q.x)); }
    void main(){
      vec3 N = normalize(vN); vec3 toEye = normalize(eye - vW); if (dot(N,toEye) < 0.) N = -N;
      vec3 eyeDir = normalize(vec3(eye.x, 0., eye.z));
      vec3 acc = vec3(0.); float ws = 0.; vec3 acc2 = vec3(0.); float ws2 = 0.;
      ${Array.from({ length: n }, (_, i) => `
      { vec4 c = VP[${i}]*vec4(vW,1.);
        if (c.w > 0.) { vec3 nd = c.xyz/c.w; vec2 uv = nd.xy*.5+.5;
          if (uv.x>=0.&&uv.x<=1.&&uv.y>=0.&&uv.y<=1.) {
            float d = nd.z*.5+.5; float sd = unpackRGBAToDepth(texture2D(dep[${i}], uv));
            vec3 toC = normalize(camP[${i}]-vW); float facing = max(dot(N,toC), 0.);
            float vis0 = clamp((sd - d + 0.0012)/0.0008, 0., 1.);
            float vis = dpar.w > .5 ? clamp((lz(sd, nf[${i}]) - c.w + dpar.x + dpar.y/max(facing, .15))/dpar.z, 0., 1.) : vis0;
            vec4 t = texture2D(tex[${i}], uv);
            vec3 cd = normalize(vec3(camP[${i}].x, 0., camP[${i}].z));
            float ang = acos(clamp(dot(cd, eyeDir), -1., 1.));
            float w = vis * t.a * pow(facing, fpow) * exp(-ang*ang/(sig[${i}]*sig[${i}])) * (isScreen > .5 ? pri[${i}] : 1.) + 1e-6*vis*t.a;
            acc += w*t.rgb; ws += w;
            float w2 = vis0 * t.a * pow(facing, fpow) * exp(-ang*ang/(sig[${i}]*sig[${i}])) + 1e-6*vis0*t.a;
            acc2 += w2*t.rgb; ws2 += w2; } } }`).join('')}
      vec3 col;
      if (ws > 1e-5) col = acc/ws;
      else if (ws2 > 1e-5) col = acc2/ws2;   // slivers no render sees under the strict test: fall back to dg-spin.js's loose test
      else { float l = .10 + .18*max(dot(N, normalize(vec3(-.4,.8,.6))),0.); col = vec3(l*.95, l*.97, l); }
      // (dg-spin.js mixed screenTex in here for isScreen > 1.5, which no part ever has -- the lit picture is the overlay mesh;
      // the sampler is dropped so that 8 views x (photo + depth map) fit WebGL's 16 texture units)
      gl_FragColor = vec4(col, 1.);
    }`;

  // ---- mesh loading -------------------------------------------------------------------------------------
  // Packed .bin (see proto/mock/duo/pkgtest/pack.js): 'DGM1' | u32 headerLen | JSON header | Float32 pos, [Int8 normals], Uint16/32 idx
  function parseBin(ab) {
    const dv = new DataView(ab);
    if (dv.getUint32(0, false) !== 0x44474d31) throw new Error('DGSpin: not a DGM1 mesh');
    const H = dv.getUint32(4, true);
    const hdr = JSON.parse(new TextDecoder().decode(new Uint8Array(ab, 8, H)));
    const D = 8 + H;
    const parts = hdr.parts.map(p => ({
      name: p.name, screen: !!p.screen,
      pos: new Float32Array(ab, D + p.pos, p.n * 3),
      nrm: p.nrm == null ? null : new Int8Array(ab, D + p.nrm, p.n * 3),
      idx: p.it === 32 ? new Uint32Array(ab, D + p.idx, p.i) : new Uint16Array(ab, D + p.idx, p.i),
    }));
    return { parts, ov: hdr.ov };
  }
  // Legacy model.json ({panel:{v,f}, base:{v,f}, ...}): split the panel's screen faces exactly as spin.js does
  function fromLegacy(mesh) {
    const parts = [];
    for (const [k, p] of Object.entries(mesh)) {
      const V = new Float32Array(p.v), F = p.f;
      if (k !== 'panel') { parts.push({ name: k, pos: V, idx: new Uint32Array(F) }); continue; }
      const sF = [], hF = [];
      for (let i = 0; i < F.length; i += 3) {
        const a = F[i] * 3, b = F[i + 1] * 3, c = F[i + 2] * 3;
        const ux = V[b] - V[a], uy = V[b + 1] - V[a + 1], uz = V[b + 2] - V[a + 2], vx = V[c] - V[a], vy = V[c + 1] - V[a + 1], vz = V[c + 2] - V[a + 2];
        let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx; const l = Math.hypot(nx, ny, nz) || 1; nz /= l;
        const cz = (V[a + 2] + V[b + 2] + V[c + 2]) / 3;
        (nz > .55 && cz > -60 ? sF : hF).push(F[i], F[i + 1], F[i + 2]);
      }
      parts.push({ name: 'hull', pos: V, idx: new Uint32Array(hF) }, { name: 'screen', screen: true, pos: V, idx: new Uint32Array(sF) });
    }
    // overlay depth profile from the panel's vertices (spin.js)
    const pv = mesh.panel.v, bins = {}, BW = 8;
    for (let i = 0; i < pv.length; i += 3) { const x = pv[i], y = pv[i + 1], z = pv[i + 2]; if (y < 260 || y > 500) continue; const b = Math.round(x / BW); if (bins[b] === undefined || z > bins[b]) bins[b] = z; }
    const keys = Object.keys(bins).map(Number).sort((a, b) => a - b), k0 = keys[0];
    return { parts, ov: { bw: BW, k0, z: Array.from({ length: keys[keys.length - 1] - k0 + 1 }, (_, i) => bins[k0 + i] ?? null) } };
  }
  async function loadMesh(url) {
    const r = await fetch(url); if (!r.ok) throw new Error('DGSpin: mesh ' + r.status);
    if (/\.json(\?|$)/i.test(url)) return fromLegacy(await r.json());
    return parseBin(await r.arrayBuffer());
  }

  async function mount(canvas, opts) {
    opts = Object.assign({}, opts || {});
    if (window.DG_TEX_O240 && typeof window.DG_TEX_O240 === 'object') opts.textures = Object.assign({}, window.DG_TEX_O240);
    const base = opts.base || '';
    const sc = Object.assign({}, opts.screen || {});
    const isPhone = (matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600) || /iPhone|Android.+Mobile/.test(navigator.userAgent);
    const maxDpr = opts.maxDpr || (isPhone ? 2.5 : 2);

    const [meta, mesh] = await Promise.all([
      fetch(opts.views || base + 'dgs-49oled-views.json').then(r => { if (!r.ok) throw new Error('DGSpin: views ' + r.status); return r.json(); }),
      loadMesh(opts.mesh || base + 'dgs-49oled-mesh.bin'),
    ]);
    // 49oled: the packaged view set has no straight rear render; DG49OLED240_factory_09 is one. Its camera comes from the
    // fitting state that produced the packaged mesh and views (proto/turntable/bodies/49oled/work/state_keep.json, which
    // reproduces all 7 packaged views exactly), in the frame of dgs-49oledo240-rear.webp (factory_09 cropped as the export
    // crops every view, 2048 x 1149). Added only to that exact view set and only when a 'rear' texture is passed.
    const REAR = {"name":"rear","img":"dgs-49oledo240-rear.webp","V":[-0.9999928,0.0,0.0037956,0.0,0.0,1.0,-0.0,0.0,-0.0037956,0.0,-0.9999928,0.0,-0.9823755,-334.066469,-1751.3062924,1.0],"P":[2.905155012,0.0,0.0,0.0,0.0,5.17820493,0.0,0.0,-0.002740075,-0.186533865,-1.010050251,-1.0,0.0,0.0,-100.502512563,0.0],"C":[5.665,334.066,-1751.297],"az":179.815,"el":1.134,"size":[2048,1149]};
    const views = meta.views.slice();
    const fv = views.find(v => v.name === 'front');
    const is49oled = views.length === 7 && fv && Math.abs(fv.C[0] - 13.075) < .01 && Math.abs(fv.C[2] - 1870.979) < .01;
    let extra = false;
    const rearUrl = opts.textures && opts.textures.rear;   // a 49hex 'rear' from the page's default keys must not land on this camera
    const rearOk = rearUrl && ((window.DG_TEX_O240 && window.DG_TEX_O240.rear === rearUrl) || /49oled/.test(rearUrl));
    if (is49oled && rearOk && !views.some(v => v.name === 'rear')) { views.push(REAR); extra = true; }
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const texs = await Promise.all(views.map(v => new Promise((res, rej) => loader.load((opts.textures && opts.textures[v.name]) || base + v.img,
      t => { t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true; t.anisotropy = 8; res(t); }, undefined, () => rej(new Error('DGSpin: texture ' + v.name))))));

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: !!opts.preserve });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, maxDpr));
    // each view needs 2 texture units (photo + depth map) and ~8 fragment uniform vectors (VP mat4, camP, sig, nf, pri);
    // 8 views = 16 units + ~70 vectors (WebGL2 guarantees 16 / 224; some WebGL1-only GPUs report 64): else the 7 packaged views
    const cap = renderer.capabilities;
    if (extra && (cap.maxTextures < 2 * views.length || cap.maxFragmentUniforms < 8 * views.length + 8)) { views.pop(); texs.pop().dispose(); extra = false; }
    const n = views.length;

    // geometry: screen faces (front-facing glass) carry a UV over their x/y box for the lit-screen texture
    const scene = new THREE.Scene();
    // lit-screen area (mm). Defaults: the 49oled active area (see header). opts.screen {screenX, screenY0, screenY1}
    // still works (symmetric rectangle, straight edges); screenX0/screenX1/curveTop/curveBottom refine it.
    // 49oled defaults = least-squares fit of the unprojected wallpaper outline (residual < 0.3 mm), with u = (x - centre) / half-width:
    //   top y = 575.5 - 1.33 u^2 - 0.42 u (curveTop < 0: lower at the ends; tiltTop: the photo's right end sits ~0.8 mm lower)
    //   bottom y = 232.66 + 0.06 u^2... written as screenY0 - curveBottom u^2 + tiltBottom u = 232.66 - 0.06 u^2 + 0.10 u.
    const custom = sc.screenX != null || sc.screenY0 != null || sc.screenY1 != null;
    const SX0 = sc.screenX0 ?? (sc.screenX != null ? -sc.screenX : -602.4), SX1 = sc.screenX1 ?? (sc.screenX != null ? sc.screenX : 602.4);
    const SY0 = sc.screenY0 ?? 232.66, SY1 = sc.screenY1 ?? 575.5, COLS = 96;
    const CT = sc.curveTop ?? (custom ? 0 : -1.33), CB = sc.curveBottom ?? (custom ? 0 : 0.06);
    const TT = sc.tiltTop ?? (custom ? 0 : -0.42), TB = sc.tiltBottom ?? (custom ? 0 : 0.10);
    const screenCanvas = document.createElement('canvas'); screenCanvas.width = 1920;
    screenCanvas.height = Math.round(1920 * (SY1 - SY0 + (CT + CB) / 3) / (SX1 - SX0));
    const screenTex = new THREE.CanvasTexture(screenCanvas);
    // view-blend width (radians) per view: 0.55 as in dg-spin.js for front/sidel/sider, narrower (0.40) for the 3/4
    // renders, whose features (chin logo, hex panel edges, post) do not land on exactly the same mesh spots as the
    // straight views': wide blending showed a faint second chin logo at the landed front view and ghost lines at the back.
    // 49oled (no straight rear render in this body; the back is covered by two rear 3/4 renders 129 deg apart): each
    // rear render sees the far half of the shell only as a grazing strip behind the stand post and cable clip, which,
    // stretched over that half, painted the post and clip onto the shell between ~150 and ~210 deg. Here the rear 3/4
    // renders get WIDER blend widths (0.70) and the facing term a higher power (6, FPOW), so each part of the shell is
    // taken from the render that faces it, not from whichever render is nearest in azimuth. (Used only without REAR.)
    // With the straight rear view added (REAR above) the back no longer depends on the 3/4 renders, and the narrow widths
    // (rear 0.40, rear 3/4 0.30) hand over quickly between the three rear renders, which disagree slightly about the
    // depth of the packaged mesh's back (wide blending showed the RGB bars doubled around 160 / 200 deg).
    const SIG = Object.assign(extra ? { rear: .40, rear34a: .30, rear34b: .30 } : { rear34a: .70, rear34b: .70 }, opts.sigmaByView || {});
    const FPOW = opts.facePow || (extra ? 5 : 6);
    const sigs = views.map(v => SIG[v.name] || opts.sigma || .55);
    // per-view visibility from the depth maps, compared in millimetres along each factory camera's axis (dg-spin.js
    // compares raw window depth with a 0.0012 bias, which at these camera distances lets anything within ~85 mm of a
    // surface pass as visible: the stand post sits 8-80 mm behind the rear shell, so the photos' post was painted onto the
    // shell as a see-through ghost post). bias = depthBias + depthSlope / facing (mm), ramp = depthRamp (mm).
    const nfs = views.map(v => { const a = v.P[10], b = v.P[14]; return new THREE.Vector2(b / (a - 1), b / (a + 1)); });
    const dpar = new THREE.Vector4(opts.depthBias ?? 5, opts.depthSlope ?? 2, opts.depthRamp ?? 3, opts.legacyDepth ? 0 : 1);
    // the monitor's front face (mesh part flagged 'screen': glass, bezel, chin) is coloured from the straight front render
    // wherever that render covers it; the angled renders' chin logo lands ~7 cm off on this mesh and showed as a second,
    // faint logo (and the side renders lightened the glass near 70 deg).
    const FV = opts.screenView || 'front';
    const pris = views.map(v => v.name === FV ? 1 : (opts.screenOthers ?? .001));
    const uniformsBase = () => ({
      tex: { value: texs }, dep: { value: [] }, VP: { value: [] }, camP: { value: views.map(v => new THREE.Vector3().fromArray(v.C)) },
      eye: { value: new THREE.Vector3() }, isScreen: { value: 0 }, power: { value: 0 }, screenTex: { value: screenTex }, sig: { value: sigs }, fpow: { value: FPOW }, nf: { value: nfs }, dpar: { value: dpar }, pri: { value: pris },
    });
    const mats = [], geos = [];
    for (const g of mesh.parts) {
      const bg = new THREE.BufferGeometry(), nv = g.pos.length / 3;
      bg.setAttribute('position', new THREE.BufferAttribute(g.pos, 3));
      const uv = new Float32Array(nv * 2);
      if (g.screen) {
        let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
        for (let i = 0; i < g.idx.length; i++) { const q = g.idx[i] * 3; x0 = Math.min(x0, g.pos[q]); x1 = Math.max(x1, g.pos[q]); y0 = Math.min(y0, g.pos[q + 1]); y1 = Math.max(y1, g.pos[q + 1]); }
        for (let i = 0; i < nv; i++) { uv[i * 2] = (g.pos[i * 3] - x0) / (x1 - x0); uv[i * 2 + 1] = (g.pos[i * 3 + 1] - y0) / (y1 - y0); }
      }
      bg.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      bg.setIndex(new THREE.BufferAttribute(g.idx, 1));
      if (g.nrm && !opts.computeNormals) bg.setAttribute('normal', new THREE.BufferAttribute(g.nrm, 3, true)); else bg.computeVertexNormals();
      // 49oled: the front-face priority covers 'bezel' too (black border + chin + chin logo are a separate mesh part on this body)
      const FP = opts.frontParts || ['bezel'];
      const u = uniformsBase(); u.isScreen.value = (g.screen || FP.indexOf(g.name) > -1) ? 1 : 0;
      const m = new THREE.ShaderMaterial({ uniforms: u, vertexShader: VS, fragmentShader: FS(n), side: THREE.DoubleSide });
      mats.push(m); geos.push(bg);
      scene.add(new THREE.Mesh(bg, m));
    }

    // clean curved overlay for the active display area (inset inside the bezel and above the chin)
    const ov = mesh.ov, bins = {}; ov.z.forEach((z, i) => { if (z !== null) bins[ov.k0 + i] = z; });
    const BW = ov.bw, keys = Object.keys(bins).map(Number).sort((a, b) => a - b);
    // 49oled: the active area runs ~5 mm past the ends of the glass part (to x = +-602.4 over the bezel, whose front keeps
    // curving forward), and the profile's end bins are only partly filled; held flat there (dg-spin-v401.js), the overlay's
    // last ~2 mm fell behind the bezel and the depth test cut it off. Beyond the second-last bin each side: linear extrapolation.
    const kLo = keys[1], kHi = keys[keys.length - 2];
    const zAt = x => { const b = x / BW;
      if (b <= kLo) return bins[kLo] + (bins[kLo] - bins[kLo + 1]) * (kLo - b);
      if (b >= kHi) return bins[kHi] + (bins[kHi] - bins[kHi - 1]) * (b - kHi);
      const i0 = Math.floor(b), i1 = i0 + 1; const z0 = bins[i0] ?? bins[keys[0]], z1 = bins[i1] ?? z0; return z0 + (z1 - z0) * (b - i0); };
    const HX = (SX1 - SX0) / 2, XC = (SX1 + SX0) / 2;
    const ovg = new THREE.BufferGeometry(); const P = [], U = [], I = [];
    for (let c = 0; c <= COLS; c++) {
      const x = SX0 + (SX1 - SX0) * c / COLS, z = zAt(x) + 1.2, u = (x - XC) / HX, q = u * u;
      P.push(x, SY0 - CB * q + TB * u, z, x, SY1 + CT * q + TT * u, z); U.push(c / COLS, 0, c / COLS, 1);
    }
    for (let c = 0; c < COLS; c++) { const a = c * 2; I.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    ovg.setAttribute('position', new THREE.Float32BufferAttribute(P, 3)); ovg.setAttribute('uv', new THREE.Float32BufferAttribute(U, 2)); ovg.setIndex(I);
    geos.push(ovg);
    // depth-tested (dg-spin.js draws it on top of everything): when the lit screen is dragged past ~75 deg, the near end
    // of the monitor now hides the part of the picture behind it instead of the picture being painted over that end.
    const ovMat = new THREE.MeshBasicMaterial({ map: screenTex, transparent: true, opacity: 0, side: THREE.FrontSide, depthWrite: false, depthTest: opts.overlayOnTop ? false : true });
    const ovMesh = new THREE.Mesh(ovg, ovMat); ovMesh.renderOrder = 2; ovMesh.visible = false;

    // per-view depth maps from the exact factory cameras
    const VPs = [], deps = [], rts = [];
    const depthMat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.DoubleSide });
    const DR = opts.depthRes || 1536;
    for (const v of views) {
      const cam = new THREE.Camera();
      cam.projectionMatrix.fromArray(v.P); cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
      cam.matrixWorldInverse.fromArray(v.V); cam.matrixWorld.copy(cam.matrixWorldInverse).invert(); cam.matrixAutoUpdate = false;
      const w = DR, h = Math.round(DR * v.size[1] / v.size[0]);
      const rt = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
      scene.overrideMaterial = depthMat; renderer.setRenderTarget(rt); renderer.setClearColor(0xffffff, 1); renderer.clear(); renderer.render(scene, cam);
      deps.push(rt.texture); rts.push(rt);
      VPs.push(new THREE.Matrix4().fromArray(v.P).multiply(new THREE.Matrix4().fromArray(v.V)));
    }
    scene.overrideMaterial = null; renderer.setRenderTarget(null); renderer.setClearColor(0x000000, 0);
    depthMat.dispose();
    scene.add(ovMesh);
    for (const m of mats) { m.uniforms.dep.value = deps; m.uniforms.VP.value = VPs; }

    // orbit camera (same height and pitch as the factory renders)
    const cam = new THREE.PerspectiveCamera(22, 1, 50, 20000);
    const target = new THREE.Vector3(0, 300, -30);
    let az = 390, el = -2.5;

    // ---- visibility gating: no GPU work while off-screen or in a hidden tab ----
    let onScreen = true, dirty = false, dead = false;
    const visible = () => onScreen && !document.hidden && !dead;
    function size() {
      const r = canvas.getBoundingClientRect(); if (!r.width || !r.height) return;
      renderer.setSize(r.width, r.height, false); cam.aspect = r.width / r.height; cam.updateProjectionMatrix();
      const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
      cam.userData.dist = Math.max(1420 / (2 * t * cam.aspect), 720 / (2 * t));
    }
    function render() {
      if (!cam.userData.dist) return;
      const a = THREE.MathUtils.degToRad(az), e = THREE.MathUtils.degToRad(el), d = cam.userData.dist;
      cam.position.set(target.x + d * Math.sin(a) * Math.cos(e), target.y + d * Math.sin(e), target.z + d * Math.cos(a) * Math.cos(e));
      cam.lookAt(target);
      for (const m of mats) m.uniforms.eye.value.copy(cam.position);
      renderer.render(scene, cam);
    }
    function draw() { if (visible()) { dirty = false; render(); } else dirty = true; }

    let power = 0;
    function setPower(p) { power = p; for (const m of mats) m.uniforms.power.value = p; ovMat.opacity = p; ovMesh.visible = p > 0.001; }

    // screen content loop: paused (not ticking) while off-screen, resumes on return
    let loop = 0, loopFn = null, loopT0 = 0;
    function tick(now) { loop = 0; if (!loopFn || !visible()) return; loopFn(screenCanvas.getContext('2d'), screenCanvas.width, screenCanvas.height, (now - loopT0) / 1000); screenTex.needsUpdate = true; draw(); loop = requestAnimationFrame(tick); }
    function screenLoop(fn) { cancelAnimationFrame(loop); loopFn = fn; loopT0 = performance.now(); tick(loopT0); }
    function stopScreen() { cancelAnimationFrame(loop); loop = 0; loopFn = null; }
    function paintScreen(fn) { fn(screenCanvas.getContext('2d'), screenCanvas.width, screenCanvas.height); screenTex.needsUpdate = true; }

    function resume() { if (!visible()) return; if (loopFn && !loop) loop = requestAnimationFrame(tick); if (dirty) draw(); }
    let io = null, ro = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(es => { for (const e of es) onScreen = e.isIntersecting; resume(); }, { rootMargin: '200px 0px' });
      io.observe(canvas);
    }
    const onVis = () => resume();
    document.addEventListener('visibilitychange', onVis);
    const onResize = () => { size(); draw(); };
    if ('ResizeObserver' in window) { ro = new ResizeObserver(onResize); ro.observe(canvas); } else addEventListener('resize', onResize);
    size(); render();

    const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    let raf = 0;
    function play(o) {
      o = o || {}; cancelAnimationFrame(raf); setPower(0);
      const from = o.from ?? 180, to = o.to ?? 0, dur = o.dur ?? 2300, t0 = performance.now();
      return new Promise(res => {
        (function step(now) {
          const t = Math.min(1, (now - t0) / dur); az = from + (to - from) * ease(t); draw();
          if (t < 1) raf = requestAnimationFrame(step);
          else {
            const p0 = performance.now();
            (function on(now2) { const q = Math.min(1, (now2 - p0) / 700); setPower(q * q * (3 - 2 * q)); draw(); if (q < 1) raf = requestAnimationFrame(on); else { res(); opts.onLanded && opts.onLanded(); } })(p0);
          }
        })(t0);
      });
    }
    // drag to turn
    let drag = null;
    const pd = e => { drag = { x: e.clientX, az }; canvas.setPointerCapture(e.pointerId); };
    const pm = e => { if (!drag) return; az = drag.az - (e.clientX - drag.x) * .35; draw(); };
    const pu = () => { drag = null; };
    canvas.addEventListener('pointerdown', pd); canvas.addEventListener('pointermove', pm); canvas.addEventListener('pointerup', pu); canvas.addEventListener('pointercancel', pu);

    function destroy() {
      dead = true; cancelAnimationFrame(raf); stopScreen();
      io && io.disconnect(); ro ? ro.disconnect() : removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
      canvas.removeEventListener('pointerdown', pd); canvas.removeEventListener('pointermove', pm); canvas.removeEventListener('pointerup', pu); canvas.removeEventListener('pointercancel', pu);
      for (const g of geos) g.dispose(); for (const m of mats) m.dispose(); ovMat.dispose();
      for (const t of texs) t.dispose(); for (const r of rts) r.dispose(); screenTex.dispose(); renderer.dispose();
    }
    return { play, setAz: d => { az = d; draw(); }, setPower: p => { setPower(p); draw(); }, paintScreen, screenLoop, stopScreen, draw, destroy };
  }
  window.DGSpin = { mount };
})();
